/**
 * AI assistant settings in the inbox (owner decision 31 Oct 2026): name, tone, style note, greeting, business hours with an
 * away message, FAQs (stored in the knowledge base, /v1/knowledge/faqs) and handover rules. Loads
 * GET /v1/whatsapp-inbox/settings/assistant + GET /v1/knowledge/faqs; saves both with PUT. Everyone in the workspace can
 * read them; only the owner or an admin can change them (the API enforces it too).
 */
import { useState } from 'react';
import type { AssistantSettings } from '@qm/contracts/whatsapp-assistant';
import { ASSISTANT_TONES, OUTSIDE_HOURS_MODES, STYLE_NOTE_MAX, MESSAGE_TEXT_MAX } from '@qm/contracts/whatsapp-assistant';
import type { FaqItem } from '@qm/contracts/knowledge';
import { Loading } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bffJson } from '../../../lib/bff';
import { useResource } from '../../../lib/resource';
import {
  ASSISTANT_PATH, assistantErrors, assistantOf, canEditAssistant, FAQS_PATH, faqsOf, keywordsOf, MAX_FAQS, MAX_HANDOVER_KEYWORDS, scheduleOf, timezonesWith, weekOf,
  type AssistantError, type DayRow,
} from './assistant-data';

interface Note { ok: boolean; text: string }

/** Save the settings, then the FAQs. Returns what the API stored. */
export async function saveAssistant(settings: AssistantSettings, faqs: readonly FaqItem[]): Promise<{ settings: AssistantSettings; faqs: FaqItem[] }> {
  const a = await bffJson<unknown>(ASSISTANT_PATH, 'PUT', settings);
  const f = await bffJson<unknown>(FAQS_PATH, 'PUT', { items: faqs.map((x) => ({ question: x.question.trim(), answer: x.answer.trim() })) });
  return { settings: assistantOf(a), faqs: faqsOf(f) };
}

export function AssistantSettingsPanel() {
  const { t, me } = useApp();
  const settings = useResource<unknown>(ASSISTANT_PATH);
  const faqs = useResource<unknown>(FAQS_PATH);
  if (settings.status === 'loading' || faqs.status === 'loading') return <Loading label={t('common.loading')} />;
  if (settings.status === 'error' || faqs.status === 'error') return <p className="cu-hint" role="alert">{t('whatsapp-inbox.assistant.loadError')}</p>;
  return <AssistantEditor initial={assistantOf(settings.data)} initialFaqs={faqsOf(faqs.data)} canEdit={canEditAssistant(me?.roles)} />;
}

export function AssistantEditor({ initial, initialFaqs, canEdit, save = saveAssistant }: {
  initial: AssistantSettings; initialFaqs: FaqItem[]; canEdit: boolean;
  save?: (s: AssistantSettings, f: readonly FaqItem[]) => Promise<{ settings: AssistantSettings; faqs: FaqItem[] }>;
}) {
  const { t } = useApp();
  const [s, setS] = useState<AssistantSettings>(initial);
  const [faqs, setFaqs] = useState<FaqItem[]>(initialFaqs);
  const [week, setWeek] = useState<DayRow[]>(weekOf(initial.businessHours.schedule));
  const [keywords, setKeywords] = useState(initial.handover.keywords.join('\n'));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const [errors, setErrors] = useState<AssistantError[]>([]);
  const p = 'whatsapp-inbox.assistant';
  const set = <K extends keyof AssistantSettings>(k: K, v: AssistantSettings[K]) => setS((x) => ({ ...x, [k]: v }));
  const setDay = (day: number, patch: Partial<DayRow>) => setWeek((w) => w.map((d) => (d.day === day ? { ...d, ...patch } : d)));
  const draft = (): AssistantSettings => ({ ...s, businessHours: { ...s.businessHours, schedule: scheduleOf(week) }, handover: { ...s.handover, keywords: keywordsOf(keywords) } });

  const submit = async () => {
    const next = draft();
    const errs = assistantErrors(next, faqs);
    setErrors(errs); setNote(null);
    if (errs.length) return;
    setBusy(true);
    try {
      const saved = await save(next, faqs);
      setS(saved.settings); setFaqs(saved.faqs); setWeek(weekOf(saved.settings.businessHours.schedule)); setKeywords(saved.settings.handover.keywords.join('\n'));
      setNote({ ok: true, text: t(`${p}.saved`) });
    } catch (e) {
      setNote({ ok: false, text: e instanceof BffError && e.status === 403 ? t('whatsapp-inbox.settings.readOnly') : e instanceof BffError && e.status === 400 ? t(`${p}.invalid`) : t('shell.error.action') });
    } finally { setBusy(false); }
  };

  return (
    <form className="cu-assistant" data-testid="inbox-assistant" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <fieldset className="cu-aimode" disabled={!canEdit || busy}>
        <legend>{t(`${p}.title`)}</legend>
        <p className="cu-hint">{t(`${p}.intro`)}</p>

        <div className="cu-assistant__grid">
          <label className="cu-field"><span>{t(`${p}.name`)}</span>
            <input name="assistant-name" value={s.name} maxLength={60} onChange={(e) => set('name', e.target.value)} />
            <small>{t(`${p}.name.help`)}</small>
          </label>
          <label className="cu-field"><span>{t(`${p}.tone`)}</span>
            <select name="assistant-tone" value={s.tone} onChange={(e) => set('tone', e.target.value as AssistantSettings['tone'])}>
              {ASSISTANT_TONES.map((tone) => <option key={tone} value={tone}>{t(`${p}.tone.${tone}`)}</option>)}
            </select>
          </label>
        </div>
        <label className="cu-field"><span>{t(`${p}.styleNote`)}</span>
          <textarea name="assistant-style" rows={2} maxLength={STYLE_NOTE_MAX} value={s.styleNote} onChange={(e) => set('styleNote', e.target.value)} />
          <small>{t(`${p}.styleNote.help`, { max: STYLE_NOTE_MAX })}</small>
        </label>

        <h3 className="cu-assistant__h">{t(`${p}.greeting`)}</h3>
        <label className="cu-check"><input type="checkbox" name="greeting-enabled" checked={s.greeting.enabled} onChange={(e) => set('greeting', { ...s.greeting, enabled: e.target.checked })} /> {t(`${p}.greeting.enabled`)}</label>
        {s.greeting.enabled ? (
          <div className="cu-assistant__grid">
            <label className="cu-field"><span>{t(`${p}.greeting.text`)}</span><textarea name="greeting-en" dir="ltr" rows={2} maxLength={MESSAGE_TEXT_MAX} value={s.greeting.text} onChange={(e) => set('greeting', { ...s.greeting, text: e.target.value })} /></label>
            <label className="cu-field"><span>{t(`${p}.greeting.textAr`)}</span><textarea name="greeting-ar" dir="rtl" lang="ar" rows={2} maxLength={MESSAGE_TEXT_MAX} value={s.greeting.textAr} onChange={(e) => set('greeting', { ...s.greeting, textAr: e.target.value })} /></label>
          </div>
        ) : null}

        <h3 className="cu-assistant__h">{t(`${p}.hours`)}</h3>
        <label className="cu-check"><input type="checkbox" name="hours-enabled" checked={s.businessHours.enabled} onChange={(e) => set('businessHours', { ...s.businessHours, enabled: e.target.checked })} /> {t(`${p}.hours.enabled`)}</label>
        {s.businessHours.enabled ? (
          <>
            <label className="cu-field"><span>{t(`${p}.hours.timezone`)}</span>
              <select name="hours-timezone" value={s.businessHours.timezone} onChange={(e) => set('businessHours', { ...s.businessHours, timezone: e.target.value })}>
                {timezonesWith(s.businessHours.timezone).map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </label>
            <ul className="cu-assistant__week">
              {week.map((d) => (
                <li key={d.day} data-testid={`hours-day-${d.day}`}>
                  <label className="cu-check"><input type="checkbox" checked={d.open} onChange={(e) => setDay(d.day, { open: e.target.checked })} /> {t(`${p}.day.${d.day}`)}</label>
                  <label><span className="qm-sr-only">{t(`${p}.hours.from`)}</span><input type="time" aria-label={`${t(`${p}.day.${d.day}`)} ${t(`${p}.hours.from`)}`} value={d.from} disabled={!d.open} onChange={(e) => setDay(d.day, { from: e.target.value })} /></label>
                  <label><span className="qm-sr-only">{t(`${p}.hours.to`)}</span><input type="time" aria-label={`${t(`${p}.day.${d.day}`)} ${t(`${p}.hours.to`)}`} value={d.to} disabled={!d.open} onChange={(e) => setDay(d.day, { to: e.target.value })} /></label>
                </li>
              ))}
            </ul>
            <small className="cu-hint">{t(`${p}.hours.overnight`)}</small>
            <fieldset className="cu-assistant__radios">
              <legend>{t(`${p}.hours.outside`)}</legend>
              {OUTSIDE_HOURS_MODES.map((m) => (
                <label key={m} className="cu-check"><input type="radio" name="hours-outside" value={m} checked={s.businessHours.outsideHours === m} onChange={() => set('businessHours', { ...s.businessHours, outsideHours: m })} /> {t(`${p}.hours.outside.${m}`)}</label>
              ))}
            </fieldset>
            <div className="cu-assistant__grid">
              <label className="cu-field"><span>{t(`${p}.hours.away`)}</span><textarea name="away-en" dir="ltr" rows={2} maxLength={MESSAGE_TEXT_MAX} value={s.businessHours.awayMessage} onChange={(e) => set('businessHours', { ...s.businessHours, awayMessage: e.target.value })} /></label>
              <label className="cu-field"><span>{t(`${p}.hours.awayAr`)}</span><textarea name="away-ar" dir="rtl" lang="ar" rows={2} maxLength={MESSAGE_TEXT_MAX} value={s.businessHours.awayMessageAr} onChange={(e) => set('businessHours', { ...s.businessHours, awayMessageAr: e.target.value })} /></label>
            </div>
          </>
        ) : null}

        <h3 className="cu-assistant__h">{t(`${p}.faqs`)} <small>{t(`${p}.faqs.count`, { count: faqs.length, max: MAX_FAQS })}</small></h3>
        <p className="cu-hint">{t(`${p}.faqs.help`, { max: MAX_FAQS })} <a href="/knowledge">{t(`${p}.faqs.knowledge`)}</a></p>
        <label className="cu-check"><input type="checkbox" name="use-knowledge" checked={s.useKnowledgeBase} onChange={(e) => set('useKnowledgeBase', e.target.checked)} /> {t(`${p}.useKnowledgeBase`)}</label>
        {faqs.length ? (
          <ol className="cu-assistant__faqs">
            {faqs.map((f, i) => (
              <li key={i} data-testid="faq-row">
                <label className="cu-field"><span>{t(`${p}.faqs.question`)}</span><input value={f.question} maxLength={300} onChange={(e) => setFaqs((x) => x.map((y, j) => (j === i ? { ...y, question: e.target.value } : y)))} /></label>
                <label className="cu-field"><span>{t(`${p}.faqs.answer`)}</span><textarea rows={2} maxLength={1000} value={f.answer} onChange={(e) => setFaqs((x) => x.map((y, j) => (j === i ? { ...y, answer: e.target.value } : y)))} /></label>
                <button type="button" className="cu-btn" data-variant="outline" onClick={() => setFaqs((x) => x.filter((_, j) => j !== i))}>{t(`${p}.faqs.remove`)}</button>
              </li>
            ))}
          </ol>
        ) : <p className="cu-hint">{t(`${p}.faqs.empty`)}</p>}
        <button type="button" className="cu-btn" data-variant="outline" disabled={faqs.length >= MAX_FAQS} onClick={() => setFaqs((x) => [...x, { question: '', answer: '' }])}>{t(`${p}.faqs.add`)}</button>

        <h3 className="cu-assistant__h">{t(`${p}.handover`)}</h3>
        <label className="cu-field"><span>{t(`${p}.handover.keywords`)}</span>
          <textarea name="handover-keywords" rows={3} value={keywords} onChange={(e) => setKeywords(e.target.value)} />
          <small>{t(`${p}.handover.keywords.help`, { max: MAX_HANDOVER_KEYWORDS })}</small>
        </label>
        <label className="cu-check"><input type="checkbox" name="handover-human" checked={s.handover.onHumanRequest} onChange={(e) => set('handover', { ...s.handover, onHumanRequest: e.target.checked })} /> {t(`${p}.handover.onHumanRequest`)}</label>

        {errors.length ? <ul className="cu-hint" role="alert">{errors.map((e) => <li key={e}>{t(`${p}.error.${e}`, { max: e === 'faqsMax' ? MAX_FAQS : MAX_HANDOVER_KEYWORDS })}</li>)}</ul> : null}
        {canEdit ? <button type="submit" className="cu-btn">{t(`${p}.save`)}</button> : <p className="cu-hint">{t('whatsapp-inbox.settings.readOnly')}</p>}
        {note ? <p role={note.ok ? 'status' : 'alert'} className="cu-hint" data-testid="assistant-note">{note.text}</p> : null}
      </fieldset>
    </form>
  );
}
